import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const IdentityDocumentExplainer = ({ align = 'left' }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const [coords, setCoords] = useState(null)
  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const popWidth = Math.min(300, window.innerWidth - 2 * margin)
      const desiredLeft = align === 'left' ? r.left : r.right - popWidth
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - popWidth - margin))
      setCoords({ top: r.bottom + 8, left })
    }
  }, [open, align])

  return (
    <Wrap>
      <InfoButton
        ref={btnRef}
        type='button'
        aria-label='What does this mean?'
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        onMouseDown={e => e.stopPropagation()}
      >
        <InfoOutlinedIcon style={{ fontSize: 16 }} aria-hidden='true' />
      </InfoButton>

      {open && coords && createPortal(
        <Pop
          ref={popRef}
          role='dialog'
          aria-label='About identity documents'
          style={{ top: coords.top, left: coords.left }}
          onClick={e => e.stopPropagation()}
        >
          <PopTitle>Identity document</PopTitle>
          <PopText>
            This is a personal ID document, such as a passport or driving licence. Only
            people your administrator has given access can open it. Guru and other AI
            features never read it.
          </PopText>
        </Pop>,
        document.body
      )}
    </Wrap>
  )
}

export default IdentityDocumentExplainer

const Wrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`

const InfoButton = styled.button`
  font: inherit;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #6e6e78;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: background-color 160ms ease, color 160ms ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    transform: translate(-50%, -50%);
  }

  &:hover {
    background: rgba(12, 18, 41, 0.08);
    color: ${({ theme }) => theme.colors.haiti};
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 1px;
  }
`

const Pop = styled.div`
  position: fixed;
  z-index: 100100;
  width: 300px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 18px 44px rgba(12, 18, 41, 0.22);
`

const PopTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const PopText = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4d4d56;
`
